<template>
    <div>
        <rkm-form
            ref="Form"
            show-legend
        >
            <rkm-row>
                <rkm-col cols="6">
                    <rkm-form-input
                        label="Unidade"
                        :value="data.form.unidade"
                        disabled
                    />
                </rkm-col>

                <rkm-col cols="6">
                    <rkm-form-input
                        label="Local de atendimento"
                        :value="data.form.infraestrutura"
                        disabled
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="6">
                    <rkm-form-date-picker
                        range
                        :range-inline="false"
                        start-label="Período de"
                        end-label="Até"
                        v-model="data.model.periodo"
                        :min-date="data.additionals.periodoMinDate"
                        :max-date="data.additionals.currentDate"
                        :start-validations="validations.periodo.start"
                        :end-validations="validations.periodo.end"
                    />
                </rkm-col>

                <rkm-col cols="6">
                    <rkm-form-input
                        label="Paciente"
                        v-model="data.model.paciente"
                        uppercase
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="3">
                    <rkm-form-select
                        label="Sexo"
                        :options="data.form.sexos"
                        default-option-text="Todos"
                        v-model="data.model.sexoId"
                        value-field="id"
                        text-field="descricao"
                        show-default-option
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-date-picker
                        label="Data de nascimento"
                        v-model="data.model.dataNascimento"
                        :max-date="data.additionals.currentDate"
                    />
                </rkm-col>

                <rkm-col>
                    <rkm-form-input
                        label="FAA"
                        v-model="data.model.faa"
                        number
                    />
                </rkm-col>

                <rkm-col>
                    <rkm-form-select
                        label="Tipo de documento"
                        :options="data.form.tiposDocumento"
                        default-option-text="Todos"
                        v-model="data.model.tipoDocumento"
                        show-default-option
                    />
                </rkm-col>

                <rkm-col cols="auto">
                    <rkm-button
                        :label="false"
                        color="primary"
                        @click="$refs.Form.validate(search)"
                    >
                        Buscar
                    </rkm-button>

                    <rkm-button
                        :label="false"
                        @click="resetForm()"
                    >
                        Limpar
                    </rkm-button>
                </rkm-col>
            </rkm-row>
        </rkm-form>

        <rkm-table
            :items="atendimentos"
            :fields="fields.tables.atendimentos"
            hide-filters
        >
            <template v-slot:cell(paciente)="{ item }">
                <rkm-button
                    :id="uniqueId(`paciente-icon-${item.tipoIdentificador}-${item.id}`)"
                    class="mr-2"
                    small
                >
                    <rkm-icon icon="user"/>
                </rkm-button>

                <rkm-popover
                    :target="uniqueId(`paciente-icon-${item.tipoIdentificador}-${item.id}`)"
                    triggers="hover"
                    placement="top"
                    title="Dados cadastrais"
                >
                    <div>
                        <span>
                            <strong>Prontuário:</strong> {{ item.pacienteProntuario }}
                        </span>
                    </div>

                    <div>
                        <span>
                            <strong>Data de nascimento:</strong> {{ item.pacienteDataNascimento }}
                        </span>
                    </div>

                    <div>
                        <span>
                            <strong>Nome da mãe:</strong> {{ item.pacienteNomeMae }}
                        </span>
                    </div>
                </rkm-popover>

                <span
                    :id="uniqueId(`paciente-nome-${item.tipoIdentificador}-${item.id}`)"
                    :class="{ 'text-primary': item.pacienteIsDesconhecido }"
                >
                    {{ item.pacienteNome }}

                    <template v-if="item.pacienteNomeSocial">
                        <component :is="item.pacientePrefereNomeSocial ? 'strong' : 'span'">({{ item.pacienteNomeSocial }})</component>
                    </template>
                </span>

                <rkm-popover
                    v-if="item.pacienteIsDesconhecido"
                    :target="uniqueId(`paciente-nome-${item.tipoIdentificador}-${item.id}`)"
                    triggers="hover"
                    placement="top"
                    title="Características físicas"
                >
                    <span>
                        {{ item.pacienteCaracteristicasFisicas }}
                    </span>
                </rkm-popover>
            </template>

            <template v-slot:cell(actions)="{ item }">
                <rkm-button
                    color="info"
                    small
                    title="Imprimir (não assinado)"
                    @click="print(item)"
                >
                    <rkm-icon icon="print" />
                </rkm-button>

                <rkm-button
                    color="primary"
                    small
                    title="Assinar"
                    @click="sign(item)"
                >
                    <rkm-icon icon="signature" />
                </rkm-button>
            </template>
        </rkm-table>

        <ModalAssinaturaEletronica
            :after-sign="afterSign"
            :ref="modalAssinaturaEletronicaRefName"
            @sign="onSign"
            allow-cancel
            no-allow-skip-sign
            v-bind="modalAssinaturaEletronicaFaaProps"
        />
    </div>
</template>

<script>
// Others
import { mapActions } from 'vuex'
import { repository, error, object, data, stringCase, prontoAtendimentoAssinaturaEletronica, file } from '@/mixins'

export default {
    computed: {
        fields() {

            return {
                tables: {
                    atendimentos: [
                        { key: 'dataHora', label: 'Data/hora', class: 'col-min' },
                        { key: 'faaNumero', label: 'FAA', class: 'col-min'  },
                        { key: 'tipo', label: 'Tipo', class: 'col-min'  },
                        { key: 'sexo', label: 'Sexo', class: 'col-min'  },
                        { key: 'paciente', label: 'Paciente' },
                        { key: 'actions', label: 'Ações', class: 'col-min text-center' },
                    ]
                }
            }
        },
        validations() {

            return {
                periodo: {
                    start: { required: true },
                    end: { required: true },
                }
            }
        }
    },
    created() {

        this.repositories = {
            impressao: this.getConectaSusRepository('impressao.prontoAtendimento'),
            assinaturaEletronica: this.getConectaSusRepository('prontoAtendimento.assinaturaEletronica'),
            atendimentoTriagem: this.getConectaSusRepository('prontoAtendimento.atendimentoTriagem'),
            atendimentoMedico: this.getConectaSusRepository('prontoAtendimento.atendimentoMedico'),
            atendimentoEnfermagem: this.getConectaSusRepository('prontoAtendimento.enfermagem'),
            pacienteObservacao: this.getConectaSusRepository('prontoAtendimento.observacao'),
        }
    },
    data: () => ({
        flg_processando : 0,
        atendimentos: []
    }),
    mixins: [repository, error, object, data, stringCase, prontoAtendimentoAssinaturaEletronica, file],
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        search() {

            this.setLoading()

            this.repositories.assinaturaEletronica.datatable(this.getSearchParams())
                .then(({ data }) => this.atendimentos = data)
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        getSearchParams() {

            const params = this.clone(this.data.model)

            this.formateDates(params.periodo, ['start', 'end'], 'DD/MM/YYYY')
            this.formateDates(params, ['dataNascimento'], 'DD/MM/YYYY')

            return this.camelObjectKeysToSnakeRecursive(params)
        },
        resetForm() {

            this.data.model.periodo.start = this.clone(this.data.initials.model.periodo.start)
            this.data.model.periodo.end = this.clone(this.data.initials.model.periodo.end)
            this.data.model.setorId = this.data.initials.model.setorId
            this.data.model.sexoId = this.data.initials.model.sexoId
            this.data.model.paciente = this.data.initials.model.paciente
            this.data.model.dataNascimento = this.clone(this.data.initials.model.dataNascimento)
            this.data.model.faa = this.data.initials.model.faa
            this.data.model.tipoDocumento = this.data.initials.model.tipoDocumento

            this.$nextTick(() => this.$refs.Form.resetValidator())
        },
        unlockLoading() {
            this.flg_processando = 0
            this.unsetLoading()
        },
        print(item) {

            if (item.tipoIdentificador == 'receita_medica') {

                const promises = []

                if (item.temReceitaMedicaComum) {

                    promises.push(() => this.repositories.impressao.atendimentoMedico.receita(
                        item.atendimentoId,
                        { receitas_ids: [item.id] }
                    ))
                }

                if (item.temReceitaMedicaEspecial) {

                    promises.push(() => this.repositories.impressao.atendimentoMedico.receitaEspecial(
                        item.atendimentoId,
                        { receitas_ids: [item.id] }
                    ))
                }

                this.setLoading()

                Promise.all(promises.map(p => p()))
                    .then(responses => responses.forEach(r => this.openFile(r.data, r.headers['content-type'])))
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            }
            else {
                if(this.flg_processando == 1){
                    return
                }
                this.flg_processando = 1
                this.setLoading()

                this.repositories.impressao.faa.eletronica(item.atendimentoId, {
                    atendimentos: [{ tipo: item.tipoIdentificador, id: item.id }]
                })
                    .then(response => this.openFile(response.data, response.headers['content-type']))
                    .catch(this.handleError)
                    .finally(() => this.unlockLoading())
            }
        },
        sign(item) {

            this.onSign = () => this.search()

            if (item.tipoIdentificador == 'receita_medica') {

                this.afterSign = receitasAssinadas => {

                    return this.repositories.atendimentoMedico.storeReceitasAssinadas(item.id, {
                        receitas_assinadas: receitasAssinadas
                    })
                }

                const promises = []
                const fileNames = []

                if (item.temReceitaMedicaComum) {

                    promises.push(() => this.repositories.impressao.atendimentoMedico.receita(
                        item.atendimentoId,
                        { receitas_ids: [item.id], para_assinatura_eletronica: true }
                    ))

                    fileNames.push('receita-medica-comum')
                }

                if (item.temReceitaMedicaEspecial) {

                    promises.push(() => this.repositories.impressao.atendimentoMedico.receitaEspecial(
                        item.atendimentoId,
                        { receitas_ids: [item.id], para_assinatura_eletronica: true }
                    ))

                    fileNames.push('receita-medica-especial')
                }

                this.setLoading()

                Promise.all(promises.map(p => p()))
                    .then(responses => {

                        const files = responses.map(r => new Blob([r.data], { type: r.headers['content-type'] }))

                        this.signReceitaMedica(files, fileNames)
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            }
            else {

                this.afterSign = faaAssinada => {

                    return this.getStoreFaaRepository(item.tipoIdentificador).storeFaaAssinada(item.id, {
                        faa_assinada: faaAssinada
                    })
                }

                this.signFaa(
                    item.atendimentoId,
                    [{ tipo: item.tipoIdentificador, id: item.id }],
                    `faa-${item.faa}-atendimento-${item.tipoIdentificador}-${item.id}`
                )
            }
        },
        getStoreFaaRepository(tipoIdentificador) {

            switch (tipoIdentificador) {

                case 'triagem': {

                    return this.repositories.atendimentoTriagem
                }
                case 'medico': {

                    return this.repositories.atendimentoMedico
                }
                case 'enfermagem': {

                    return this.repositories.atendimentoEnfermagem
                }
                case 'observacao': {

                    return this.repositories.pacienteObservacao
                }
            }
        }
    },
    props: {
        data: { type: Object, required: true }
    },
    watch: {
        'data.model': {
            deep: true,
            handler() {

                this.atendimentos = []
            }
        }
    }
}
</script>